import React, { ReactNode, useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from "@mui/icons-material/Delete";

import { RetryImage } from "../../../components/RetryImage";

import DefaultTable from "../../../components/Table/DefaultTable";
import { deleteOption, getSearchOptions } from "../../../apis/option";
import { useGlobalSnackBar } from "../../SnackbarContext";
import { CustomDialog } from "../../../components/modal/CustomDialog";
import { useModelData } from "../../../hook/useModelData";
import { filteredModels } from "../../../utils/model";
import { Option } from "../../../utils/option";
import { Loader } from "../../../components/Loader";
import { RootState } from "../../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { setOptionState } from "../../../store/actions";

interface TRowData<T> {
    [key: string]: T;
}

const headerData: string[] = [
    "Option",
    "BF Part #",
    "Category",
    "Applicable Models",
    "MSRP",
    "",
];
const headerColumnWidth: number[] = [20, 10, 10, 35, 10, 15];

const sortableFields: any = {
    "BF Part #": "part_number",
    Option: "option_name",
    Category: "parent_id",
    MSRP: "MSRP",
};

interface StatCardProps {
    value: number | string;
    description: string;
}

const StatCard: React.FC<StatCardProps> = ({ value, description }) => (
    <Grid item sm={3} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <Card sx={{ width: "60%", minWidth: "180px", textAlign: "center" }}>
            <CardContent sx={{ pb: "10px !important" }}>
                <Typography variant="h5" component="div">
                    {value}
                </Typography>
                <Typography sx={{ mb: 1.5 }} color="text.secondary">
                    {description}
                </Typography>
            </CardContent>
        </Card>
    </Grid>
);

export const Options: React.FC = () => {
    const history = useNavigate();
    const { showSnackBar } = useGlobalSnackBar();
    const dispatch = useDispatch();

    const [options, setOptions] = useState<TRowData<ReactNode>[]>([]);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [optionId, setOptionId] = useState<string | null>(null);
    const [categories, setCategories] = useState<any | []>([]);
    const [isLoading, setIsLoading] = useState<boolean | false>(true);
    const [count, setCount] = useState<number>(0);
    const currentUser = useSelector((state: RootState) => state.adminAuth.admin);

    const optionState = useSelector(
        (state: RootState) => state.adminAuth.optionState
    );

    const { models, loading: modelLoading } = useModelData();

    const fetchOptions = async () => {
        try {
            const {
                data: optionData,
                counts,
                categoryData,
            } = await getSearchOptions({
                page: optionState.page + 1,
                pageSize: optionState.pageSize,
                orderBy: optionState.orderBy && sortableFields[optionState.orderBy],
                order: optionState.order,
            });

            setCategories(categoryData);
            const transformedData = optionData.map((option: Option) => {
                const imageUrl = typeof JSON.parse(option.images)?.[0] === "string"
                    ? `${process.env.REACT_APP_COMPANY_LOGO_URL}/${JSON.parse(option.images)?.[0]}`
                    : undefined;

                return {
                    option: (
                        <Box
                            component={Link}
                            to={`/options/${option.ID}`}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.5,
                                textDecoration: "none",
                                color: "inherit",
                                py: 0.5,
                            }}
                        >
                            <RetryImage
                                sx={{ width: 90, height: 90, borderRadius: 1 }}
                                variant="square"
                                src={imageUrl}
                            />
                            <Typography variant="body2" fontWeight={500} sx={{ color: "primary.main", textAlign: "center" }}>
                                {option.option_name}
                            </Typography>
                        </Box>
                    ),
                    part: option.part_number,
                    category: categoryData.find(
                        (category: any) => category.ID === option.parent_id
                    )?.option_name,
                    applicable_models: filteredModels(
                        models,
                        JSON.parse(option?.applicable_model_ids)
                    ),
                    msrp: `$${option.MSRP?.toFixed(2)}`,
                    edit: (
                        <div>
                            <Tooltip title="Edit">
                                <IconButton size="small" color="primary" onClick={() => history(`/options/${option.ID}`)}>
                                    <ModeEditIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                        setOptionId(option.ID);
                                        setOpenDeleteModal(true);
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </div>
                    ),
                };
            });
            setOptions(transformedData);
            setCount(counts);
            setIsLoading(false);
        } catch (error) {
            console.error("Failed to fetch admins", error);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        if (!modelLoading) {
            fetchOptions();
        }
    }, [modelLoading, optionState]);

    // Function to handle sorting
    const handleSort = (column: string) => {
        if (!sortableFields[column]) return;
        const isAsc = optionState.orderBy === column && optionState.order === "asc";
        dispatch(setOptionState({ order: isAsc ? "desc" : "asc", orderBy: column }));
    };

    const permissionErrorMessage = () => {
        showSnackBar("Invalid operation: Only Administrators can make this change.", "error");
    };

    const onDelete = async () => {
        if (currentUser?.permission !== "Administrator") {
            permissionErrorMessage();
            return;
        }
        try {
            await deleteOption({ ID: optionId || "" });
            showSnackBar("Delete this option");
            fetchOptions();
            setOpenDeleteModal(false);
        } catch (err) {
            showSnackBar("Error", "error");
        }
    };

    const handleChangePage = (page: number) => {
        dispatch(setOptionState({ page }));
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setOptionState({ page: 0, pageSize: Number(e.target.value) }));
    };

    if (modelLoading || isLoading) return <Loader isLoading={isLoading} />;

    return (
        <>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <StatCard value={count} description="Options" />
                    <StatCard value={categories?.length} description="Categories" />
                    <Grid item sm={3} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                        <Card
                            style={{ textAlign: "center", cursor: "pointer" }}
                            onClick={() => {
                                if (currentUser?.permission !== "Administrator") {
                                    permissionErrorMessage();
                                    return;
                                }
                                history("/options/create");
                            }}
                            sx={{ width: "60%", minWidth: "180px", textAlign: "center" }}
                        >
                            <CardContent sx={{ pb: "10px !important" }}>
                                <Typography variant="h5" component="div">
                                    +
                                </Typography>
                                <Typography sx={{ mb: 1.5 }} color="text.secondary">
                                    Add Options
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item sm={3} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                        <Card
                            style={{ textAlign: "center", cursor: "pointer" }}
                            onClick={() => history("/options/categories")}
                            sx={{ width: "60%", minWidth: "180px", textAlign: "center" }}
                        >
                            <CardContent sx={{ pb: "10px !important" }}>
                                <Typography variant="h5" component="div">
                                    +
                                </Typography>
                                <Typography sx={{ mb: 1.5 }} color="text.secondary">
                                    Manage Category
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ flexGrow: 1, mt: 2 }}>
                <DefaultTable
                    headerData={headerData}
                    headerColumnWidth={headerColumnWidth}
                    rowsData={options}
                    rowsPerPage={optionState.pageSize}
                    page={optionState.page}
                    count={count}
                    handleChangePage={(
                        e: React.ChangeEvent<HTMLInputElement>,
                        page: number
                    ) => handleChangePage(page)}
                    handleChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeRowsPerPage(e)
                    }
                    handleSort={handleSort}
                    order={optionState.order}
                    orderBy={optionState.orderBy}
                    sortableFields={sortableFields}
                />
            </Box>

            <CustomDialog
                title="Are you sure you want to delete this option?"
                openModal={openDeleteModal}
                handleClose={() => setOpenDeleteModal(false)}
                handleAction={onDelete}
                buttonTitle={"Yes"}
                children={undefined}
            />
        </>
    );
};
